/**
 * Engine-neutral 2D geometric helpers for hit testing and spatial checks.
 *
 * Kept free of camera/world types so any canvas engine can pass raw
 * numbers (screen or world space, the helpers don't care). For engine-
 * specific hit testers (GraphHitTester, DataTable hitTesting), compose
 * these with layout/camera data locally.
 */

/**
 * Return true if point `(px, py)` is inside the axis-aligned rectangle
 * starting at `(rx, ry)` with size `(rw, rh)`. Right/bottom edges are
 * inclusive to match canvas-click conventions.
 */
export function isPointInRect(
  px: number,
  py: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
): boolean {
  return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
}

/**
 * Euclidean distance between `(px, py)` and the center `(cx, cy)`.
 * Uses `Math.hypot` (handles overflow/underflow correctly vs. `sqrt(x*x+y*y)`).
 */
export function pointDistance(px: number, py: number, cx: number, cy: number): number {
  return Math.hypot(px - cx, py - cy);
}

/** Return true if `(px, py)` is inside the circle centered at `(cx, cy)` with `radius`. */
export function isPointInCircle(
  px: number,
  py: number,
  cx: number,
  cy: number,
  radius: number
): boolean {
  return pointDistance(px, py, cx, cy) <= radius;
}

/**
 * Shortest distance from `(px, py)` to the line segment `(ax, ay)`—`(bx, by)`.
 * Handles zero-length segments by returning the point-to-endpoint distance.
 */
export function distToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return pointDistance(px, py, ax, ay);
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  return pointDistance(px, py, projX, projY);
}

/** Return true if `(px, py)` lies within `tolerance` of segment `(ax, ay)`—`(bx, by)`. */
export function isPointNearSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  tolerance: number
): boolean {
  return distToSegment(px, py, ax, ay, bx, by) <= tolerance;
}

/**
 * Return true if `(px, py)` lies within `tolerance` of any segment of the
 * polyline `points`. Points with `< 2` entries never hit.
 */
export function isPointNearPolyline(
  px: number,
  py: number,
  points: ReadonlyArray<{ x: number; y: number }>,
  tolerance: number
): boolean {
  for (let i = 0; i < points.length - 1; i++) {
    if (
      isPointNearSegment(
        px,
        py,
        points[i].x,
        points[i].y,
        points[i + 1].x,
        points[i + 1].y,
        tolerance
      )
    ) {
      return true;
    }
  }
  return false;
}

export type CanvasGeometryPoint = { x: number; y: number };

export type RoundedOrthogonalPathSegment = {
  lineEnd: CanvasGeometryPoint;
  control?: CanvasGeometryPoint;
  curveEnd?: CanvasGeometryPoint;
};

export type RoundedOrthogonalPathPlan = {
  start: CanvasGeometryPoint;
  segments: RoundedOrthogonalPathSegment[];
};

/** Builds a renderer-neutral sequence of lines and quadratic rounded corners. */
export function getRoundedOrthogonalPathPlan(
  points: readonly CanvasGeometryPoint[],
  cornerRadius: number
): RoundedOrthogonalPathPlan | undefined {
  const start = points[0];
  if (!start) {
    return undefined;
  }
  const segments: RoundedOrthogonalPathSegment[] = [];
  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const corner = points[index];
    const next = points[index + 1];
    const incomingX = corner.x - previous.x;
    const incomingY = corner.y - previous.y;
    const outgoingX = next.x - corner.x;
    const outgoingY = next.y - corner.y;
    const incomingLength = Math.hypot(incomingX, incomingY);
    const outgoingLength = Math.hypot(outgoingX, outgoingY);
    const radius = Math.min(cornerRadius, incomingLength / 2, outgoingLength / 2);
    const cross = incomingX * outgoingY - incomingY * outgoingX;
    if (radius <= 0 || Math.abs(cross) < 0.001) {
      segments.push({ lineEnd: corner });
      continue;
    }
    segments.push({
      lineEnd: {
        x: corner.x - (incomingX / incomingLength) * radius,
        y: corner.y - (incomingY / incomingLength) * radius,
      },
      control: corner,
      curveEnd: {
        x: corner.x + (outgoingX / outgoingLength) * radius,
        y: corner.y + (outgoingY / outgoingLength) * radius,
      },
    });
  }
  const target = points.at(-1);
  if (target && target !== start) {
    segments.push({ lineEnd: target });
  }
  return { start, segments };
}
